import React, {useId, useState, useMemo, useEffect} from 'react'
/** @jsx jsx */
import {jsx, css} from '@emotion/react'
import {useIntl, FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {Box, Flex, Heading, HStack, Text, useBreakpointValue} from '@chakra-ui/react'
import Carousel from '@app/components/carousel/index.jsx'
import {Component} from '@salesforce/commerce-sdk-react/components'
import usePDCResponsiveElement from '@app/hooks/use-pdc-responsive-element.js'
import {useToast} from '@app/hooks/use-toast.js'
import {ArrowButton} from '@app/components/arrow-button/index.jsx'
import {HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive/index.jsx'
import {LoaderIcon} from '@app/components/custom-icons/index.jsx'

/**
 * CarouselButtons component renders navigation buttons for a carousel.
 *
 * This component provides "previous" and "next" arrow buttons to navigate through
 * a carousel. The buttons are conditionally disabled based on the current slide
 * position and the total number of slides to show.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.swiper - The swiper instance used to control the carousel.
 * @param {number} props.currentSlide - The index of the currently active slide.
 * @param {number} props.nbSlidesToShow - The number of slides to display at once.
 *
 * @returns {JSX.Element} A stack containing the navigation arrow buttons.
 */
const CarouselButtons = ({swiper, currentSlide, nbSlidesToShow}) => {
    return (
        <HStack gap={6}>
            <ArrowButton
                direction="prev"
                disabled={currentSlide === 0}
                onClick={() => swiper?.slickPrev()}
            />
            <ArrowButton
                direction="next"
                onClick={() => swiper?.slickNext()}
                disabled={
                    currentSlide === swiper?.innerSlider?.props?.children?.length - nbSlidesToShow
                }
            />
        </HStack>
    )
}

CarouselButtons.propTypes = {
    swiper: PropTypes.object,
    currentSlide: PropTypes.number,
    nbSlidesToShow: PropTypes.number
}

/**
 * PDCStyleGuideCarousel is a React functional component that renders a carousel of style guide tiles.
 *
 * This component is designed to display a collection of tiles grouped by their respective group IDs.
 * It dynamically adjusts the number of slides shown based on the screen size and provides navigation
 * controls for browsing through the tiles. The component also handles the parsing of a JSON string
 * that maps group IDs to their corresponding titles, displaying an error toast if parsing fails.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.groupsMapping - A JSON string that maps group IDs to their titles.
 * @param {number} props.smallNbSlidesToShow - Number of slides to show on small screens.
 * @param {number} props.mediumNbSlidesToShow - Number of slides to show on medium screens.
 * @param {number} props.largeNbSlidesToShow - Number of slides to show on large screens.
 * @param {number} props.xlargeNbSlidesToShow - Number of slides to show on extra-large screens.
 * @param {number} props.xxlargeNbSlidesToShow - Number of slides to show on double extra-large screens.
 * @param {Array} props.regions - An array of region objects containing tile components.
 *
 * @returns {JSX.Element} The rendered carousel component.
 */
export const PDCStyleGuideCarousel = ({
    groupsMapping,
    smallNbSlidesToShow,
    mediumNbSlidesToShow,
    largeNbSlidesToShow,
    xlargeNbSlidesToShow,
    xxlargeNbSlidesToShow,
    isPDP,
    selectedModelId,
    regions
}) => {
    const intl = useIntl()
    const [swiper, setSwiper] = useState()
    const titleId = useId()
    const toast = useToast()
    const [isLoading, setIsLoading] = useState(true)
    const [isClient, setIsClient] = useState(false)
    const [currentSlide, setCurrentSlide] = useState(0)

    useEffect(() => {
        setIsClient(true) // Set to true when the component is mounted on the client
    }, [])

    // Get the tiles components from the regions
    const tiles = regions?.find((region) => region.id === 'pdc_style_guide_tiles')?.components || []

    // Dynamically generate the order of groupIds from tiles
    const groupOrder = [...new Set(tiles.map((tile) => tile.data.groupId))]

    // Sort tiles based on groupId order
    const sortedTiles = tiles.sort(
        (a, b) => groupOrder.indexOf(a.data.groupId) - groupOrder.indexOf(b.data.groupId)
    )

    // Group tiles by groupId
    const tilesByGroup = sortedTiles.reduce((acc, tile) => {
        const groupId = tile.data.groupId
        acc[groupId] = acc[groupId] || []
        acc[groupId].push(tile)
        return acc
    }, {})

    // Find index of the selected tile
    const selectedTileIndex = sortedTiles.findIndex((tile) => tile.data.modelId === selectedModelId)
    const initialSlideIndex = selectedTileIndex >= 0 ? selectedTileIndex : 0

    // Get the number of slides to show depending on the screen size
    const nbSlidesToShowByBreakpoint = usePDCResponsiveElement({
        smallEl: smallNbSlidesToShow || 2,
        mediumEl: mediumNbSlidesToShow,
        largeEl: largeNbSlidesToShow,
        xlargeEl: xlargeNbSlidesToShow,
        xxlargeEl: xxlargeNbSlidesToShow
    })
    const currentNbSlidesToShow = useBreakpointValue(nbSlidesToShowByBreakpoint)

    // variableWidth = false on large PDP
    const isVariableWidth = useBreakpointValue({
        base: true,
        md: true,
        lg: !isPDP
    })

    /* Parse the groupsMapping JSON string into an array of objects
     * containing group IDs and titles. If parsing fails, display an error toast.
     */
    const mapping = useMemo(() => {
        try {
            return JSON.parse(groupsMapping)
        } catch (e) {
            toast({
                title: intl.formatMessage({
                    id: 'styleguide.carousel.toast.error.title',
                    defaultMessage: 'Error parsing Groups ID - Name mapping'
                }),
                description: intl.formatMessage({
                    id: 'styleguide.carousel.toast.error.description',
                    defaultMessage:
                        'Please check the format of the "Groups ID - Name mapping" field in the component configuration.'
                }),
                status: 'error'
            })
        }
    }, [groupsMapping])

    /**
     * Retrieves the title of a group based on the provided group ID.
     *
     * This function searches through a predefined mapping of groups to find a group
     * that matches the given group ID. If a matching group is found, it returns the
     * title of that group. If no matching group is found, it defaults to returning
     * the provided group ID.
     *
     * @param {string} groupId - The unique identifier of the group whose title is to be retrieved.
     * @returns {string} The title of the group if found; otherwise, the provided group ID.
     */
    const getGroupTitle = (groupId) => {
        const group = mapping?.find((group) => group.id === groupId)
        return group ? group?.title : groupId // Fallback to groupId if title is not found
    }

    /**
     * Updates the opacity of headings within a carousel by toggling a CSS class
     * based on the active state of slides. This function iterates over all slides
     * in the carousel and applies the 'first-active-heading' class to the heading
     * of the first active slide encountered. The class is removed from all other
     * headings to ensure only one heading has the class at any given time.
     *
     * The function also manages a boundary condition using the presence of
     * specific slides marked as 'first' and 'last' to determine when to start
     * and stop applying the class.
     *
     * @function updateHeadingOpacity
     * @returns {void}
     */
    const updateHeadingOpacity = () => {
        const slides = swiper?.innerSlider?.list?.querySelectorAll('.slick-slide')
        let isWithinBounds = false

        slides?.forEach((slide) => {
            const isFirst = slide.querySelector('.pdc-style-guide-carousel-slide-first')
            const isLast = slide.querySelector('.pdc-style-guide-carousel-slide-last')
            const isActive = slide.classList.contains('slick-active')
            const heading = slide.querySelector('.pdc-style-guide-carousel-title')

            if (isFirst) {
                isWithinBounds = true
            }

            if (isWithinBounds && isActive && heading) {
                heading.classList.add('first-active-heading')
                isWithinBounds = false // Stop after the first active slide
            } else if (heading) {
                heading.classList.remove('first-active-heading')
            }

            if (isLast) {
                isWithinBounds = false
            }
        })
    }

    useEffect(() => {
        if (isClient) {
            updateHeadingOpacity()
            if (swiper) {
                setIsLoading(false)
            }
        }
    }, [swiper, isClient])

    const carouselSettings = {
        dots: false,
        arrows: false,
        infinite: false,
        speed: 400,
        slidesToShow: currentNbSlidesToShow,
        centerMode: false,
        variableWidth: isVariableWidth,
        initialSlide: initialSlideIndex,
        onInit: () => {
            setIsLoading(false)
        },
        onReInit: () => {
            setIsLoading(false)
        },
        beforeChange: (prev, next) => {
            setTimeout(() => {
                setCurrentSlide(next)
                updateHeadingOpacity()
            }, 50)
        }
    }

    /**
     * Generates an array of styled components based on the provided tiles and their grouping.
     *
     * This function maps over the `sortedTiles` array to create a list of styled components,
     * each wrapped in a `Box` component. It determines whether each tile is the first or last
     * in its group and applies specific CSS classes accordingly. Additionally, it sets properties
     * such as `isPDP` and `selectedModelId` on each tile.
     *
     * @param {Array} sortedTiles - An array of tile objects that have been sorted.
     * @param {Object} tilesByGroup - An object mapping group IDs to arrays of tiles.
     * @param {boolean} isPDP - A flag indicating if the current view is a PDP (Product Detail Page).
     * @param {string} selectedModelId - The ID of the selected model.
     * @param {number} currentNbSlidesToShow - The current number of slides to display.
     * @returns {Array} An array of React components, each representing a styled tile.
     */
    const styleComponents = useMemo(() => {
        return sortedTiles.map((tile, index) => {
            const isFirstInGroup = tilesByGroup[tile.data.groupId][0] === tile
            const isLastInGroup =
                tilesByGroup[tile.data.groupId][tilesByGroup[tile.data.groupId].length - 1] === tile
            tile.isPDP = isPDP
            tile.selectedModelId = selectedModelId
            tile.nbSlides = currentNbSlidesToShow
            return (
                <Box
                    as={tiles.length > currentNbSlidesToShow ? 'div' : 'li'}
                    key={tile?.id || index}
                    className={`
                ${isFirstInGroup && !isPDP ? 'pdc-style-guide-carousel-slide-first' : ''}
                ${isLastInGroup && !isPDP ? 'pdc-style-guide-carousel-slide-last' : ''}
            `.trim()}
                >
                    {!isPDP && (
                        <Heading
                            as="h2"
                            size="4xl"
                            mb={18}
                            className="pdc-style-guide-carousel-title"
                        >
                            {getGroupTitle(tile.data.groupId)}
                        </Heading>
                    )}
                    <Component component={tile} />
                </Box>
            )
        })
    }, [sortedTiles, tilesByGroup, isPDP, selectedModelId, currentNbSlidesToShow])

    return (
        <Box
            as="section"
            aria-describedby={titleId}
            css={[PDCStyleGuideCarousel.css]}
            pt={isPDP ? 0 : 25}
        >
            {isClient && isLoading && (
                <Flex justifyContent="center" py={50}>
                    <LoaderIcon boxSize={20} />
                </Flex>
            )}

            <Box display={isClient && isLoading ? 'none' : 'block'}>
                {isPDP && (
                    <Flex
                        flex={1}
                        px={[10, null, null, 20]}
                        pt={['16px', null, '4px']}
                        pb={[20, null, null, 10]}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                    >
                        <Text variant="bodyLarge2" as={'h2'}>
                            <FormattedMessage
                                defaultMessage="Our Styles"
                                id="product_details_carousel.title.style"
                            />
                        </Text>
                        {tiles && tiles.length > currentNbSlidesToShow && (
                            <HideOnMobile>
                                <CarouselButtons
                                    swiper={swiper}
                                    currentSlide={currentSlide}
                                    nbSlidesToShow={currentNbSlidesToShow}
                                />
                            </HideOnMobile>
                        )}
                    </Flex>
                )}
                {tiles && tiles.length > currentNbSlidesToShow ? (
                    <Box
                        ml={isPDP ? [10, null, 20] : 20}
                        mr={[0, null, isPDP ? 0 : 20]}
                        className={
                            isPDP
                                ? 'pdc-style-guide-carousel-wrapper-pdp'
                                : 'pdc-style-guide-carousel-wrapper'
                        }
                    >
                        <Carousel
                            settings={carouselSettings}
                            label={intl.formatMessage({
                                id: 'styleguide.carousel.label',
                                defaultMessage: 'Style guide carousel'
                            })}
                            setSwiper={setSwiper}
                            nbSlidesToShowByBreakpoint={nbSlidesToShowByBreakpoint}
                        >
                            {styleComponents}
                        </Carousel>
                    </Box>
                ) : (
                    <Flex
                        as="ul"
                        listStyleType="none"
                        display="flex"
                        justifyContent="center"
                        mx={20}
                        sx={{
                            '& > *': {
                                flex: 1
                            }
                        }}
                        gap={10}
                        className="pdc-style-guide-list"
                    >
                        {styleComponents}
                    </Flex>
                )}
                {!isPDP && tiles && tiles.length > currentNbSlidesToShow && (
                    <Flex
                        flex={1}
                        pt={['16px', null, '4px']}
                        pb={['16px', null, '23px']}
                        mx={['18px', null, '39px']}
                        justifyContent={'flex-end'}
                        alignItems={'flex-end'}
                    >
                        <CarouselButtons
                            swiper={swiper}
                            currentSlide={currentSlide}
                            nbSlidesToShow={currentNbSlidesToShow}
                        />
                    </Flex>
                )}
            </Box>
        </Box>
    )
}

PDCStyleGuideCarousel.css = css`
    overflow: hidden;

    .slick-track {
        display: flex;
        padding-right: 60px;
        @media screen and (min-width: 1200px) {
            padding-right: 0;
        }
    }

    .slick-slide {
        height: inherit;
        margin-left: 5px;
        margin-right: 5px;
        flex: 1;
    }

    /* Slides groups styles (not on PDP) */
    /* group boundary: pdc-style-guide-carousel-slide-first - pdc-style-guide-carousel-slide-last */
    .pdc-style-guide-carousel-wrapper {
        .slick-slide {
            &:has(.pdc-style-guide-carousel-slide-first) {
                margin-left: 10px;
            }
            &:has(.pdc-style-guide-carousel-slide-last) {
                margin-right: 10px;
            }

            @media screen and (min-width: 768px) {
                &:has(.pdc-style-guide-carousel-slide-first) {
                    margin-left: 20px;
                }
                &:has(.pdc-style-guide-carousel-slide-last) {
                    margin-right: 20px;
                }
                &:last-child {
                    margin-right: 0;
                }
            }
        }
    }

    .pdc-style-guide-list {
        .pdc-style-guide-carousel-slide-first {
            margin-left: 10px;
        }
        .pdc-style-guide-carousel-slide-last {
            margin-right: 10px;
        }

        @media screen and (min-width: 768px) {
            .pdc-style-guide-carousel-slide-first {
                margin-left: 20px;
            }
            .pdc-style-guide-carousel-slide-last {
                margin-right: 20px;
            }
            li:first-of-type {
                margin-left: 0;
            }
            li:last-child {
                margin-right: 0;
            }
        }
    }

    .pdc-style-guide-carousel-title {
        transition: opacity 0.2s, transform 0.2s ease;
        opacity: 0;
        transform: translateX(-100%);
    }

    .pdc-style-guide-carousel-slide-first {
        .pdc-style-guide-carousel-title {
            opacity: 1;
            transform: translateX(0);
        }
    }

    .pdc-style-guide-carousel-title.first-active-heading {
        opacity: 1;
        transform: translateX(0);
    }
`

PDCStyleGuideCarousel.propTypes = {
    groupsMapping: PropTypes.string,
    smallNbSlidesToShow: PropTypes.number,
    mediumNbSlidesToShow: PropTypes.number,
    largeNbSlidesToShow: PropTypes.number,
    xlargeNbSlidesToShow: PropTypes.number,
    xxlargeNbSlidesToShow: PropTypes.number,
    isPDP: PropTypes.bool,
    selectedModelId: PropTypes.string,
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    )
}
